//------------------------------------------------------------------------------
// ColumnDetailModal
//
//NOTE: isReportDefChanged in Functions will need to be updated to check for
//  column changes when/if this is implemented
//------------------------------------------------------------------------------
import React, { useEffect, useState } from "react";

import PropTypes from 'prop-types'
import { Button, Form, Input, InputNumber, Modal, Select, Switch, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { getReportTableColumnFilterValues,handleReportTableFilter } from "./Functions";

const { TextArea } = Input;

const ReportColumnsDetailModal = ({
  isReportColumnsDetailModalOpen, 
  setIsReportColumnsDetailModalOpen,
  reportDefRef,
  reportTableColumns,
  setReportTableColumns,
  reportTableExtraData,
  forceRerender,
  setForceRerender,
  width,
}) => {
  if (!reportDefRef.current) {  //Return if a reportDefRef object does not exist
    console.log('Bad or missing reportDefRef')
    return (<></>)
  }
  const [form] = Form.useForm();
  const [selectedFields, setSelectedFields] = useState(reportDefRef?.current?.selectedFields)
  const [selectedFieldName, setSelectedFieldName] = useState(reportDefRef?.current?.selectedFields[0].fieldName)

  const selectedField = selectedFields.find((field) => field.fieldName === selectedFieldName);

  useEffect(() => {
    if (isReportColumnsDetailModalOpen && reportDefRef) {
      const initialValues = reportDefRef.current.selectedFields[0]
      form.setFieldsValue(initialValues);
    }
  }, [isReportColumnsDetailModalOpen, reportDefRef, form]);

  //User selected a new field to modify
  const handleSelectedFieldChange = (value) => {
    setSelectedFieldName(value)
    const newSelectedField = selectedFields.find((field) => field.fieldName === value);
    form.setFieldsValue(newSelectedField)
  }

  //Handle a change to the currently selected field
  const handleFieldChange = (key, value) => {
    setSelectedFields((prev) =>
      prev.map((field) =>
        field.fieldName === selectedFieldName ? { ...field, [key]: value } : field
      )
    );
  };

  const handleCancel = () => {
    setIsReportColumnsDetailModalOpen(false)
  }
  
  const handleOk = () => {
    form.validateFields().then(values => {
      // update reportDefRef
      reportDefRef.current = { ...reportDefRef.current, selectedFields };

      // immutably clone columns array first
      const updatedReportTableColumns = [...reportTableColumns];
      // or deep-copy: JSON.parse(JSON.stringify(reportTableColumns))


      for (const selectedField of selectedFields) {
        const columnIndex = updatedReportTableColumns.findIndex(
          column => column.dataIndex === selectedField.fieldName
        );
        if (columnIndex === -1) continue;

        const column = { ...updatedReportTableColumns[columnIndex] };

        column.title = (
          <span>
            {selectedField.tooltipText && (
              <Tooltip title={selectedField.tooltipText}>
                <InfoCircleOutlined style={{ cursor: 'pointer' }} />{' '}
              </Tooltip>
            )}
            <span className="dragHandler">{selectedField.fieldCaption}</span>
          </span>
        );
        column.align = selectedField.fieldAlignment;
        column.width = selectedField.fieldWidth;
        column.sorter = !!selectedField.fieldHasSort;
        column.tooltipText = selectedField.tooltipText ?? '';

        if (selectedField.fieldHasFilter) {
          column.onFilter = (value, record) =>
            handleReportTableFilter(value, record, selectedField.fieldName, reportDefRef);
          column.filters = getReportTableColumnFilterValues(
            selectedField.fieldName,
            reportTableExtraData,
            reportDefRef
          );
          column.filterIcon = undefined;
          column.filterDropdown = undefined;
        } else {
          delete column.filters;
          delete column.onFilter;
          delete column.filterDropdown;
          delete column.filterIcon;
        }

        updatedReportTableColumns[columnIndex] = column;
      }

      setReportTableColumns(updatedReportTableColumns);
      setIsReportColumnsDetailModalOpen(false);
    });
  };
  
  return (
    <Modal
      // title={
      //   <div 
      //     style={{
      //       backgroundColor: '#e6f7ff',
      //       padding: '12px 24px',
      //       fontWeight: 'bold',
      //       fontSize: '16px',
      //       borderBottom: '1px solid #d9d9d9',
      //       margin: '-16px -24px 16px -24px' 
      //     }}>
      //     Report Columns Detail
      //   </div>
      // }
      title="Report Columns Detail"
      open={isReportColumnsDetailModalOpen}
      closable={false}
      keyboard={false}
      footer={[
        <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
        <Button key="ok" type="primary" onClick={handleOk}>OK</Button>,
      ]}
      //width={width}
    >
      <Form
        //name='reportColumnsDetailModal'
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }} // Controls label width (right-aligned)
        wrapperCol={{ span: 18 }} // Controls input width (left-aligned)
        style={{ padding: '24px', gap: '24px' }} // Add padding or gap

      >
        <Form.Item
          name='fieldSelector'
          label='Select a column:'
          initialValue={reportDefRef?.current?.selectedFields[0].fieldName}
        >
          <Select
            
            onChange={(value) => handleSelectedFieldChange(value)}
            //onChange={(value) => setSelectedFieldName(value)}

            //onChange={(key) => setSelectedFieldName(key)}
            value={selectedFieldName}
            style={{ width: '100%', marginBottom: '16pt' }}
            placeholder={'Select a column to edit'}
            >
            {selectedFields.map((field) => (
              <Option key={field.fieldName} value={field.fieldName}>
                {field.fieldCaption}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          name='fieldCaption'
          label='Caption:'
        >
          <Input
            type='text'
            placeholder="Enter a caption for the column header"
            value={selectedField?.fieldCaption}
            onChange={(e) => handleFieldChange('fieldCaption', e.target.value)}
            disabled={!selectedFieldName}
            style={{ width: '100%' }} 
          ></Input>
        </Form.Item>

        <Form.Item 
          name='tooltipText'
          label='Info Popup Text:'
        >
          <TextArea
            type='text'
            placeholder="Enter a description of the column's content"
            value={selectedField?.tooltipText}
            onChange={(e) => handleFieldChange('tooltipText', e.target.value)}
            disabled={!selectedFieldName}
            autoSize={{ minRows: 2, maxRows: 8 }}
            style={{ width: '100%' }}
            maxLength={500}
          ></TextArea>
        </Form.Item>

        <Form.Item
          className='form-item-text'
          label='Alignment'
          name='fieldAlignment'
        >
          <Select
            defaultValue='left'
            style={{ width: '150px' }}
            value={selectedField?.fieldAlignment}
            onChange={(value) => handleFieldChange('fieldAlignment', value)}
            disabled={!selectedFieldName}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
              { value: 'right', label: 'Right' },
            ]}
          />
        </Form.Item>

        {/* <Form.Item 
          name='fieldWidth'
          label='Width:'
        >
          <InputNumber
            placeholder="Enter a caption for the column header"
            value={selectedField?.fieldWidth}
            onChange={(value) => handleFieldChange('fieldWidth', value)}
            disabled={!selectedFieldName}
            defaultValue={50}
            min={1}
            max={1000}
            step={1}
            precision={0}
          />
        </Form.Item> */}

        <Form.Item label="Has Sorter">
          <Switch
            checked={selectedField?.fieldHasSort}
            onChange={(checked) => handleFieldChange('fieldHasSort', checked)}
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled={!selectedFieldName}
          />
        </Form.Item>
        
        <Form.Item label="Has Filter">
          <Switch
            checked={selectedField?.fieldHasFilter}
            onChange={(checked) => handleFieldChange('fieldHasFilter', checked)}
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled={!selectedFieldName}
          />
        </Form.Item>

      </Form>
    </Modal>
  )
}

// ReportColumnsDetailtModal.propTypes = {
  // isReportColumnsDetailModalOpen: PropTypes.bool.isRequired,
  // setIsReportColumnsDetailModalOpen: PropTypes.func.isRequired,
  // reportDefRef: PropTypes.object.isRequired,
// }

export default ReportColumnsDetailModal;
